import axios from "axios";
import config from "../../config.json";

const ACCEPTED_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
  "application/pdf",
];

export const MAX_SUPPORT_ATTACHMENTS = 5;

export const getSupportAttachmentUrl = (filename) => {
  if (!filename) return "";
  const base = String(config.UPLOADS_SERVER || "").replace(/\/+$/, "");
  if (!base) return "";
  return `${base}/support/${filename}`;
};

export const createSupport = async ({
  type,
  description,
  recommendation = "",
  customType = "",
  subcategory = "",
  customSubcategory = "",
  priority = "moyenne",
  issueStarted = "today",
  preferredContactMethod = "email",
  attachments = [],
}) => {
  try {
    const formData = new FormData();
    formData.append("type", type);
    formData.append("description", description);
    formData.append("priority", priority);
    formData.append("issueStarted", issueStarted);
    formData.append("preferredContactMethod", preferredContactMethod);
    if (recommendation) formData.append("recommendation", recommendation);
    if (customType) formData.append("customType", customType);
    if (subcategory) formData.append("subcategory", subcategory);
    if (customSubcategory) formData.append("customSubcategory", customSubcategory);
    attachments.forEach((file) => formData.append("attachments", file));

    const response = await axios.post(`${config.dev}/support/create`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error creating support request:", error);
    throw error.response?.data || { message: "Error creating support request" };
  }
};

export const validateSupportAttachments = (files) => {
  if (!files?.length) return null;
  if (files.length > MAX_SUPPORT_ATTACHMENTS) {
    return `You can upload up to ${MAX_SUPPORT_ATTACHMENTS} files`;
  }
  for (const file of files) {
    if (!ACCEPTED_ATTACHMENT_TYPES.includes(file.type)) {
      return "Only screenshots (JPG, PNG, WEBP, GIF) and PDF files are allowed";
    }
    if (file.size > 10 * 1024 * 1024) {
      return "Each file must be 10 MB or smaller";
    }
  }
  return null;
};

export const getAllSupports = async (page = 1, limit = 10, search = "", type = "", status = "") => {
  try {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", limit);
    if (search) params.append("search", search);
    if (type) params.append("type", type);
    if (status) params.append("status", status);

    const response = await axios.get(`${config.dev}/support/all?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching support requests:", error);
    throw error.response?.data || { message: "Error fetching support requests" };
  }
};

export const getMySupports = async ({ archived = false } = {}) => {
  try {
    const params = new URLSearchParams();
    if (archived) params.append("archived", "true");
    const query = params.toString();
    const response = await axios.get(
      `${config.dev}/support/my-supports${query ? `?${query}` : ""}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching my support requests:", error);
    throw error.response?.data || { message: "Error fetching support requests" };
  }
};

export const getSupportById = async (id) => {
  try {
    const response = await axios.get(`${config.dev}/support/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching support request:", error);
    throw error.response?.data || { message: "Error fetching support request" };
  }
};

export const updateSupportStatus = async (id, status) => {
  try {
    const response = await axios.put(
      `${config.dev}/support/status/${id}`,
      { status },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating support status:", error);
    throw error.response?.data || { message: "Error updating support status" };
  }
};

export const updateSupport = async (id, data) => {
  try {
    const response = await axios.put(`${config.dev}/support/${id}`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating support request:", error);
    throw error.response?.data || { message: "Error updating support request" };
  }
};

export const archiveMySupport = async (id) => {
  try {
    const response = await axios.put(
      `${config.dev}/support/${id}`,
      { hiddenByRequester: true },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error archiving support request:", error);
    throw error.response?.data || { message: "Error archiving support request" };
  }
};

export const restoreMySupport = async (id) => {
  try {
    const response = await axios.put(
      `${config.dev}/support/${id}`,
      { hiddenByRequester: false },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error restoring support request:", error);
    throw error.response?.data || { message: "Error restoring support request" };
  }
};

export const replyToSupport = async (id, message, attachments = []) => {
  try {
    const merged = [...attachments];
    const validationError = validateSupportAttachments(merged);
    if (validationError) {
      throw { message: validationError };
    }

    const formData = new FormData();
    formData.append("message", message || "");
    formData.append("kind", "client_reply");
    merged.forEach((file) => formData.append("attachments", file));

    const response = await axios.post(`${config.dev}/support/${id}/reply`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error replying to support request:", error);
    throw error.response?.data || error || { message: "Error replying to support request" };
  }
};

export const deleteSupport = async (id) => {
  try {
    const response = await axios.delete(`${config.dev}/support/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting support request:", error);
    throw error.response?.data || { message: "Error deleting support request" };
  }
};
